import React, { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, ChevronDown } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import './ExportMenu.css';

function ExportMenu({ onExportExcel, onExportPdf, entityName }) {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExport = async (exportFn, format) => {
    setIsExporting(true);
    try {
      const response = await exportFn();
      const blob = new Blob([response.data], {
        type: format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityName.toLowerCase()}-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${entityName} exported successfully`);
    } catch (err) {
      toast.error(`Failed to export ${entityName}`);
    } finally {
      setIsExporting(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="export-menu" ref={menuRef}>
      <button
        className="btn btn-secondary export-btn"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Export ${entityName}`}
      >
        <Download size={16} />
        Export
        <ChevronDown size={14} className={`export-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="export-dropdown" role="menu">
          {onExportExcel && (
            <button
              className="export-option"
              role="menuitem"
              onClick={() => handleExport(onExportExcel, 'excel')}
              disabled={isExporting}
            >
              <FileSpreadsheet size={16} aria-hidden="true" />
              <span>Export to Excel</span>
            </button>
          )}
          {onExportPdf && (
            <button
              className="export-option"
              role="menuitem"
              onClick={() => handleExport(onExportPdf, 'pdf')}
              disabled={isExporting}
            >
              <FileText size={16} aria-hidden="true" />
              <span>Export to PDF</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ExportMenu;
